import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Table } from "react-bootstrap";
import { db } from "../../db";
import {
  closeConflicts,
  draft,
  scopeLabel,
  useConflictDialog
} from "../../hooks/draft";
import { describe, merge } from "../../hooks/draft/diff";
import { dependentsOfConflicts } from "../../hooks/draft/resolve";
import { useClipboardCopy } from "../../hooks/useClipboard";
import useUserFields from "../../hooks/useUserFields";
import styles from "./styles.module.scss";

const EMPTY = [];

// The row of a conflict as the user knows it
const rowOf = ({ type, accessor, id }) => {
  const table = type ?? accessor;
  const row = db.peek(table, id);

  return { table, id, title: row?.title ?? row?.reference ?? "" };
};

// The title of a row, for the labels of "What changed" (a variant is known
// by the title of its block)
export const titleOf = (accessor, id) => {
  const row = db.peek(accessor, id);
  return row?.title ?? row?.reference;
};

export const when = (date) => (date ? new Date(date).toLocaleString() : "");

const propertyTitle = (id) => db.peek("properties", id)?.title;

// The drafts a reload of these conflicts discards whole, as the user knows
// them: their structural changes (variants or pages added, copied or
// deleted) were made together with a conflicting row, and must not outlive
// it. conflicts: of a 409 ({ type, id }) or live ({ accessor, id }).
export const wholeDraftsOf = (conflicts) =>
  [
    ...new Set(
      dependentsOfConflicts(draft, conflicts).map(({ scope }) => scope)
    )
  ].map((scope) => scopeLabel(scope, propertyTitle));

// The sentence that says so (the dialog, the Reload of the SaveBar).
// action: what discards, e.g. "Discard".
export const dependentsSentence = (whole, action) =>
  `Some of your unsaved changes add, copy or delete variants or pages together with these items: ${action} also discards them, with all the unsaved changes of ${whole.join(
    ", "
  )}.`;

// A row deleted on the server while it was being edited: a live conflict
// "delete" (the socket, who and when may be known), or a 409 with no
// stored version (a colleague deleted it: no version, no time). "Discard my
// changes and reload" removes it (the reload finds no stored row).
export const isDeleted = ({ action, storedVersion, reason }) =>
  reason !== "exists" && (action === "delete" || storedVersion === null);

// A rename, or a new item, onto an id a colleague created meanwhile (a 409
// with reason "exists": the stored row is theirs, not a version of mine)
export const isTaken = ({ reason }) => reason === "exists";

// Our own save come back under a form edited on an older version shown
// meanwhile (conflict.own: the store recognised the row as the one our save
// stored, see OUR OWN SAVES): never "<me> saved this item"
export const isOwn = (conflict) =>
  Boolean(conflict?.own) && !isDeleted(conflict) && !isTaken(conflict);

export const ownSentence = ({ lastSaved }) =>
  `Your save${
    lastSaved?.date ? ` of ${when(lastSaved.date)}` : ""
  } is on the server; this form was edited on an older version shown meanwhile.`;

// The choices of our own save come back: reload it (the edits made on the
// older version go), or apply those edits on it
export const OWN_RELOAD = "Load my saved version";
export const OWN_REBASE = "Apply my edits on it";

export const takenSentence = ({ id, lastSaved }) =>
  `An item with id «${id}» was created by ${
    lastSaved?.username ?? "a colleague"
  }${lastSaved?.date ? ` on ${when(lastSaved.date)}` : ""}: choose another id.`;

// --- "What changed" ----------------------------------------------------------

const GROUPS = [
  ["theirs", "Their changes"],
  ["mine", "Your changes"],
  ["both", "Changed by both"]
];

const NONE = "—";

function LineText({ line }) {
  const { label, side, kind, base, theirs, mine } = line;
  const value = side === "mine" ? mine : theirs;

  if (kind === "count" || kind === "tree")
    return (
      <>
        <b>{label}</b>:{" "}
        {side === "both" ? (
          <>
            theirs: {theirs ?? NONE}; yours: {mine ?? NONE}
          </>
        ) : (
          value
        )}
      </>
    );
  if (side === "both")
    return (
      <>
        <b>{label}</b>: was <span className={styles.old}>{base ?? NONE}</span>,
        theirs <span className={styles.new}>{theirs ?? NONE}</span>, yours{" "}
        <span className={styles.new}>{mine ?? NONE}</span>
      </>
    );
  if (kind === "added" || kind === "removed")
    return (
      <>
        <b>{label}</b> <i>{kind}</i>
      </>
    );
  return (
    <>
      <b>{label}</b>: <span className={styles.old}>{base ?? NONE}</span> →{" "}
      <span className={styles.new}>{value ?? NONE}</span>
    </>
  );
}

// The lines of describe(), grouped by side. names: the title of each group
// (e.g. the preview has only "mine": "Applied on their version").
export function ChangeLines({ lines = EMPTY, more = 0, names = {} }) {
  if (!lines.length)
    return <div className={styles.noChange}>No change in the fields.</div>;

  return (
    <div className={styles.changes}>
      {GROUPS.map(([side, name]) => {
        const group = lines.filter((line) => line.side === side);
        if (!group.length) return null;
        return (
          <div key={side} className={styles.group} data-side={side}>
            <div className={styles.groupName}>{names[side] ?? name}</div>
            <ul>
              {group.map((line, index) => (
                <li key={`${line.path}:${index}`}>
                  <LineText line={line} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {more > 0 ? <div className={styles.more}>+{more} more</div> : null}
    </div>
  );
}

// The versions of a conflict: { base, theirs } (lane 1's conflictVersions;
// none for a 409 the store knows nothing of)
const storeVersionsOf = (accessor, id) =>
  draft.conflictVersions?.(accessor, id);

// What changed in a row of the dialog, computed when it is opened
function WhatChanged({ accessor, id, mineOf, versionsOf = storeVersionsOf }) {
  const result = useMemo(() => {
    const versions = versionsOf(accessor, id);
    if (!versions || versions.theirs === undefined) return null;
    const mine = mineOf?.(accessor, id);
    return describe({
      base: versions.base,
      theirs: versions.theirs,
      // Mine not known: their changes alone
      mine: mine === undefined ? versions.base : mine,
      accessor,
      titleOf
    });
  }, [accessor, id, mineOf, versionsOf]);

  if (!result)
    return (
      <div className={styles.noChange}>
        What they changed is not known here: reload to see it.
      </div>
    );
  return <ChangeLines lines={result.lines} more={result.more} />;
}

// --- Rebase: my changes applied on their version ----------------------------

const REASONS = {
  deleted: (title) => `${title} was deleted by a colleague`,
  removed: (title) => `you deleted ${title}`,
  created: (title) => `${title} was created in your draft`,
  exists: (title) =>
    `an item with the id of ${title} was created by a colleague`,
  unknown: (title) => `what changed in ${title} is not known`
};

const named = ({ title, id }) => `«${title || id}»`;

// Can my changes go on their version, for every conflict? Computed on demand
// (the dialog opens, Apply is clicked), on a snapshot of my values. items:
// [{ accessor, id, title, row (merged), version (theirs), base, theirs,
// mine }]; blocked: [{ accessor, id, title, why, labels? }] (labels: the
// fields changed by both). versionsOf(accessor, id): { base, theirs } (the
// store's conflictVersions by default; the banner has its own).
export function rebasePlan(
  conflicts = EMPTY,
  mineOf,
  versionsOf = storeVersionsOf
) {
  const items = [];
  const blocked = [];

  for (const conflict of conflicts) {
    const { table: accessor, id, title } = rowOf(conflict);
    const item = { accessor, id, title };
    if (isTaken(conflict)) {
      blocked.push({ ...item, why: "exists" });
      continue;
    }
    if (isDeleted(conflict)) {
      blocked.push({ ...item, why: "deleted" });
      continue;
    }
    const versions = versionsOf(accessor, id);
    const mine = mineOf?.(accessor, id);
    if (!versions || versions.theirs === undefined || mine === undefined) {
      blocked.push({ ...item, why: "unknown" });
      continue;
    }

    const { base, theirs } = versions;
    const result = merge({ base, theirs, mine, accessor });
    if (result.ok) {
      items.push({
        ...item,
        row: result.row,
        version: theirs.version,
        base,
        theirs,
        mine
      });
      continue;
    }
    if (result.reason !== "both") {
      blocked.push({ ...item, why: result.reason });
      continue;
    }
    const clashes = describe({
      base,
      theirs,
      mine,
      accessor,
      titleOf,
      limit: Infinity
    }).lines.filter(({ side }) => side === "both");
    blocked.push({
      ...item,
      why: "both",
      labels: [...new Set(clashes.map(({ label }) => label))]
    });
  }

  return {
    ok: items.length > 0 && blocked.length === 0,
    items,
    blocked
  };
}

// Why my changes can't go on their version (one sentence)
export function blockedSentence(blocked = EMPTY) {
  const both = blocked.filter(({ why }) => why === "both");
  const other = blocked.filter(({ why }) => why !== "both");
  const parts = [
    ...both.map(
      (item) =>
        `${item.labels.join(", ")} of ${named(item)} ${
          item.labels.length === 1 ? "was" : "were"
        } changed both by you and by them`
    ),
    ...other.map((item) => (REASONS[item.why] ?? REASONS.unknown)(named(item)))
  ];
  if (!parts.length) return "";

  return `Your changes can't be applied on their version: ${parts.join("; ")}.${
    both.length
      ? " Put those fields back as they saved them to apply the rest, or reload."
      : ""
  }`;
}

// The preview: what my changes do to their version (merged against theirs:
// every line is "mine")
export function RebasePreview({ items = EMPTY, own = false }) {
  return (
    <div className={styles.preview}>
      <p>
        {own
          ? "The edits made on the older version, applied on your saved version. Nothing of your save is overwritten; the result is not saved until you save it."
          : "Your changes, applied on the version they saved. Nothing of theirs is overwritten; the result is not saved until you save it."}
      </p>
      {items.map((item) => {
        const { lines, more } = describe({
          base: item.theirs,
          theirs: item.theirs,
          mine: item.row,
          accessor: item.accessor,
          titleOf
        });
        return (
          <div
            key={`${item.accessor}:${item.id}`}
            className={styles.previewItem}
          >
            <div className={styles.previewTitle}>
              {item.accessor} {item.title ? <b>{item.title} </b> : null}
              <small>{item.id}</small>
            </div>
            <ChangeLines
              lines={lines}
              more={more}
              names={{ mine: "Applied on their version" }}
            />
          </div>
        );
      })}
    </div>
  );
}

// The preview in its own window (the banner of a form, the SaveBar): Confirm
// runs onConfirm(items).
// own: the version is our own save (the edits were made on an older one)
export function RebaseModal({
  plan,
  busy = false,
  failure = null,
  own = false,
  onConfirm,
  onCancel
}) {
  return (
    <Modal show={Boolean(plan)} onHide={onCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {own
            ? "Apply your edits on your saved version"
            : "Apply your changes on their version"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {plan?.ok ? (
          <RebasePreview items={plan.items} own={own} />
        ) : (
          <p className="text-warning">{blockedSentence(plan?.blocked)}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        {failure ? (
          <span className="text-danger me-auto">{failure}</span>
        ) : null}
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {plan?.ok ? "Back" : "Close"}
        </Button>
        {plan?.ok ? (
          <Button
            variant="primary"
            onClick={() => onConfirm(plan.items)}
            disabled={busy}
          >
            Confirm
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}

// What a rebase that did not go returns or throws, as the user reads it
export const rebaseFailure = (result) =>
  result?.refused?.length
    ? `Not applied: ${result.refused.length} item${
        result.refused.length === 1 ? "" : "s"
      } changed again meanwhile. Look at what changed and try again, or reload.`
    : null;

// --- Copy my values ----------------------------------------------------------

// The clipboard of the Item's Copy ({ _ares: accessor, item, tenant }), with
// my values: one row as the Item copies it (Paste takes it), several as a
// list of those. mine: true, the user's own values of the row (the Item's
// Paste puts them back as they were: no " [Copy]" title, the refs of the
// row it has).
export function clipOf(rows, tenant) {
  const clips = rows.map(({ accessor, row }) => ({
    _ares: accessor,
    item: row,
    tenant,
    mine: true
  }));
  return JSON.stringify(clips.length === 1 ? clips[0] : clips);
}

// --- The dialog ----------------------------------------------------------------

function ConflictRow({ conflict, mineOf, versionsOf }) {
  const [open, setOpen] = useState(false);
  const { table, id, title } = rowOf(conflict);
  const { lastSaved, storedVersion } = conflict;
  const deleted = isDeleted(conflict);
  const taken = isTaken(conflict);

  return (
    <>
      <tr>
        <td>{table}</td>
        <td>
          {title ? <b>{title} </b> : null}
          <small>{id}</small>
        </td>
        {taken ? (
          <td colSpan={2} className="text-warning">
            {takenSentence(conflict)}
          </td>
        ) : (
          <>
            <td>
              {deleted ? (
                <>
                  <i>deleted by a colleague</i>
                  {lastSaved?.username ? ` (${lastSaved.username})` : null}
                </>
              ) : isOwn(conflict) ? (
                <i>you (your save)</i>
              ) : (
                (lastSaved?.username ?? "?")
              )}
            </td>
            <td>{storedVersion === null ? "" : when(lastSaved?.date)}</td>
          </>
        )}
        <td className={styles.toggleCell}>
          {taken || deleted ? null : (
            <Button
              size="sm"
              variant="link"
              className={styles.toggle}
              aria-expanded={open}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? "▾" : "▸"} What changed
            </Button>
          )}
        </td>
      </tr>
      {open ? (
        <tr className={styles.detailRow}>
          <td colSpan={5}>
            <WhatChanged
              accessor={table}
              id={id}
              mineOf={mineOf}
              versionsOf={versionsOf}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

// Someone else saved some of the items being edited (a save refused with a
// 409, a live conflict refused before sending, or "Show changes"). Nothing
// was written. Per row: who and when, and what changed (theirs, mine, both)
// when opened. The choices:
// - "Discard my changes to these items and reload": onDiscard (the other
//   unsaved changes stay, except the drafts that add, copy or delete
//   variants or pages together with these items: discarded whole);
// - "Apply my changes on their version" (onRebase given, not on main, and
//   every row merges: no field changed by both, nothing deleted, no row
//   created in the draft): a preview, then Confirm calls onRebase(items)
//   (rebasePlan's items: the merged row of each);
// - "Copy my values" (mineOf given): the clipboard format of the Item's Copy;
// - "Keep editing": closes (onKeepEditing, if given).
// versionsOf(accessor, id) (optional): { base, theirs } of a row the store
// has no conflict of (the store's conflictVersions otherwise).
export default function ConflictDialog() {
  const dialog = useConflictDialog();
  const { branch, tenant } = useUserFields("branch", "tenant");
  const { copy } = useClipboardCopy();
  const [busy, setBusy] = useState(false);
  // The reload or the rebase failed: the dialog stays open and says why
  const [failure, setFailure] = useState(null);
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(false);
  const conflicts = dialog?.conflicts ?? EMPTY;
  const mineOf = dialog?.mineOf;
  const onRebase = dialog?.onRebase;
  // The rows of a conflict the store does not know (e.g. the endpoint pinned
  // by the variant page): the opener's versionsOf(accessor, id)
  const versionsOf = dialog?.versionsOf ?? storeVersionsOf;
  // The drafts discarded whole: their structural changes go with these
  // items
  const whole = wholeDraftsOf(conflicts);

  useEffect(() => {
    setFailure(null);
    setPreview(null);
    setCopied(false);
  }, [dialog]);

  // Computed once when the dialog opens, on a snapshot of my values (the
  // dialog is modal: they don't change while it is open)
  const plan = useMemo(
    () =>
      onRebase && branch !== "main" && conflicts.length
        ? rebasePlan(conflicts, mineOf, versionsOf)
        : null,
    [branch, conflicts, mineOf, onRebase, versionsOf]
  );

  // Every row is our own save come back (the form was edited on an older
  // version shown meanwhile): it says so, not "someone else"
  const own = conflicts.length > 0 && conflicts.every(isOwn);

  // The last plan: the one of the preview asked, else the one of the open
  const current = preview ?? plan;
  const rebasable = Boolean(current?.ok);
  const blocked = current && !current.ok ? current.blocked : null;

  const keepEditing = useCallback(() => {
    const onKeepEditing = dialog?.onKeepEditing;
    closeConflicts();
    onKeepEditing && onKeepEditing();
  }, [dialog]);

  const discard = () => {
    setBusy(true);
    setFailure(null);
    Promise.resolve(dialog?.onDiscard?.())
      .then(() => closeConflicts())
      .catch((error) => {
        console.error("⚠️ RELOAD AFTER CONFLICT", error);
        setFailure(String(error?.message ?? error));
      })
      .finally(() => setBusy(false));
  };

  // A fresh plan: my values may have changed since the dialog opened
  const showPreview = () => {
    setFailure(null);
    setPreview(rebasePlan(conflicts, mineOf, versionsOf));
  };

  const confirmRebase = () => {
    setBusy(true);
    setFailure(null);
    Promise.resolve(onRebase(preview.items))
      .then((result) => {
        const refused = rebaseFailure(result);
        if (refused) {
          setFailure(refused);
          setPreview(null);
        } else closeConflicts();
      })
      .catch((error) => {
        console.error("⚠️ REBASE AFTER CONFLICT", error);
        setFailure(String(error?.message ?? error));
      })
      .finally(() => setBusy(false));
  };

  const copyMine = () => {
    const rows = conflicts
      .map((conflict) => {
        const { table: accessor, id } = rowOf(conflict);
        return { accessor, row: mineOf?.(accessor, id) };
      })
      .filter(({ row }) => row);
    if (!rows.length) return;
    copy(clipOf(rows, tenant));
    setCopied(true);
  };

  return (
    <Modal show={Boolean(dialog)} onHide={keepEditing} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {preview
            ? own
              ? "Apply your edits on your saved version"
              : "Apply your changes on their version"
            : own
              ? "Your save is on the server"
              : "Someone else changed the items you are editing"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {preview?.ok ? (
          <RebasePreview items={preview.items} own={own} />
        ) : (
          <>
            <p>
              {own
                ? "Your save of these items is on the server; they were edited here on an older version shown meanwhile. This save is refused, so that what you saved is not overwritten: nothing was written."
                : "These items were saved (or deleted) by someone else after you started editing them. Your save is refused, so that their work is not overwritten: nothing was written."}
            </p>
            <Table size="sm" variant="dark" striped className={styles.rows}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Item</th>
                  <th>Changed by</th>
                  <th>On</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {conflicts.map((conflict) => {
                  const { table, id } = rowOf(conflict);
                  return (
                    <ConflictRow
                      key={`${table}:${id}`}
                      conflict={conflict}
                      mineOf={mineOf}
                      versionsOf={versionsOf}
                    />
                  );
                })}
              </tbody>
            </Table>
            {whole.length ? (
              <p className="text-warning">
                {dependentsSentence(whole, "Discard")}
              </p>
            ) : null}
            {blocked ? (
              <p className="text-warning" data-rebase-blocked>
                {blockedSentence(blocked)}
              </p>
            ) : null}
            <p>
              Keep editing (your save is refused again until you reload
              {rebasable ? " or apply your changes on their version" : ""}), or
              discard your changes to these items and reload them as they are
              stored now (
              {whole.length
                ? `your other unsaved changes stay, except those of ${whole.join(
                    ", "
                  )}`
                : "your other unsaved changes stay"}
              ).
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {failure ? (
          <span className="text-danger me-auto">{failure}</span>
        ) : copied ? (
          <span className="text-success me-auto">
            Your values were copied to the clipboard
          </span>
        ) : null}
        {preview?.ok ? (
          <>
            <Button
              variant="secondary"
              onClick={() => setPreview(null)}
              disabled={busy}
            >
              Back
            </Button>
            <Button variant="primary" onClick={confirmRebase} disabled={busy}>
              Confirm
            </Button>
          </>
        ) : (
          <>
            {mineOf ? (
              <Button
                variant="outline-secondary"
                onClick={copyMine}
                disabled={busy}
              >
                Copy my values
              </Button>
            ) : null}
            <Button variant="outline-danger" onClick={discard} disabled={busy}>
              {own
                ? OWN_RELOAD
                : "Discard my changes to these items and reload"}
            </Button>
            {rebasable ? (
              <Button
                variant="outline-primary"
                onClick={showPreview}
                disabled={busy}
              >
                {own ? OWN_REBASE : "Apply my changes on their version"}
              </Button>
            ) : null}
            <Button variant="primary" onClick={keepEditing} disabled={busy}>
              Keep editing
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
